import PropTypes from 'prop-types';
import makeStyles from '@mui/styles/makeStyles';
import {Divider, FormControl, MenuItem, Select, TextField} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';
import {useTranslation} from 'react-i18next';
import clsx from 'clsx';
import {useTheme} from '@mui/material/styles';
import React, {Fragment, useEffect, useState} from 'react';
import _ from 'lodash';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
// import Autocomplete from '@mui/material/Autocomplete';
import InputAdornment from '@mui/material/InputAdornment';
import AgyleDictionaryField from './agyleDictionaryField';

const useStyles = makeStyles((theme) => ({
    border: {
        borderRadius: 8,
        '& .MuiSelect-outlined': {
            padding: '6px 8px !important',
        },
        '&.MuiSelect-iconOpen': {
            paddingLeft: 5,
        },
        '&.smaller': {
            height: 32,
        },
        '&.small': {
            height: 36,
        },
        '&.medium': {
            height: 40,
        },
        '&.large': {
            height: 44,
        },

    },
    optionDisabled: {
        color: theme.palette.text.disabled,
    },
    renderValue: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        padding: '0px 8px',
        gap: 10,
    },
    box: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        borderRadius: 6,
        minWidth: 20,
        height: 20,
        '&.background': {
            backgroundColor: theme.palette.primary.main,
            color: '#ffffff',
        },
        '&.large': {
            width: 24,
            height: 24,
        },
    },
    label: {
        fontFamily: 'Inter',
        fontWeight: 400,
        fontSize: 12,
        color: theme.palette.mode === 'light' ? '#494B4C' : '#ffffff',
        '&.medium': {
            fontSize: 14,
        },
        '&.large': {
            fontSize: 16,
        },
    },
    menu: {
        backgroundColor: ({withNewBackground}) => withNewBackground ? `${theme.palette.inPatient.inputBackground} !important` : theme.palette.mode === 'light' ? '#ffffff' : '#313233',
    },
    divider: {
        margin: 10,
    },
    menuItem: {
        padding: 0,
        fontSize: 12,
        paddingRight: 10,
    },
    menuItemNone: {
        paddingRight: 10,
    },
    textBox: {
        color: 'white',
        opacity: 1,
        '&::placeholder': {
            color: 'white',
            opacity: 1, // otherwise firefox shows a lighter color
            fontFamily: 'Inter',
            fontWeight: 400,
            fontSize: 14,
        },
    },
}));

const AgyleMultiselectAutocomplete = (props) => {
    const classes = useStyles({withNewBackground: props.withNewBackground});
    const {t} = useTranslation();
    const theme = useTheme();

    const [valuesMap, setValuesMap] = useState({});
    const [values, setValues] = useState([]);
    const [options, setOptions] = useState([]);
    const [optionsMap, setOptionsMap] = useState([]);
    const [textValue, setTextValue] = useState('');
    const [hideDisabled, setHideDisabled] = useState(props.hideOptionInitialState);

    // Sort options, if some object is selected then it should be on top of options
    useEffect(() => {
        const iteratees = [
            (o) => {
                if (o?.disabled) {
                    return 1;
                }
                return 0;
            },
            (o) => {
                return _.find(props.value, {[props.comparePath]: o?.[props.comparePath]});
            },
            props.optionLabel,
        ];
       const sortedOptions = _.orderBy(_.filter(props.options, Boolean), iteratees, ['asc']);
        const initOptions = [
            {
                __id: 'select_none',
                __description: props.selectNoneLabel ? props.selectNoneLabel : t('Select None'),
            }
        ]
        if (props.hideOption) {
            initOptions.push({
                __id: 'hide_disabled',
                __description: t(props.hideDisabledLabel),
            })
        }

        const options = hideDisabled ? _.filter(sortedOptions, op => !op?.disabled) : sortedOptions;

        setOptions([...initOptions, ...options]);
        const _optionsMap = _.reduce(props.options,
            (obj, param) => {
                if (param) obj[param?.[props.pathToItemValue] ? param[props.pathToItemValue] : param] = param;
                return obj;
            },
            {});
        setOptionsMap(_optionsMap);
        if (props.value && props.value.length > 0) {
            setValuesMap(_.reduce(props.value,
                (obj, param) => {
                    obj[param?.[props.pathToItemValue] ? param[props.pathToItemValue] : param] = 1;
                    return obj;
                },
                {}));
        } else {
            setValuesMap({});
        }
        if (props.value && props.value.length > 0) {
            const _values = _.filter(props.value, v => {
                return !_optionsMap[v.__id]?.disabled;
            });
            setValues(_values);
            setTextValue(getFirstSelectedLabel(_values, _optionsMap));
        } else {
            setValues([]);
            setTextValue(props.label);
        }
        //count only enabled values
    }, [props.options, props.value, hideDisabled]);

    const getFirstSelectedLabel = (_values, _optionsMap) => {
        if (_values.length === 1) {
            return _optionsMap?.[_values[0].__id]?.[props.optionLabel] || '';
        }
        return props.label;
    };
    const selectItem = (option, e) => {
        if (option?.disabled) return;
        let clone = [...values];
        if (option?.__id === 'select_none') {
            props.onChange([], e);
            return;
        } else if (option?.__id === 'hide_disabled') {
            if (hideDisabled) {
                setHideDisabled(false)
            } else {
                setHideDisabled(true);
            }
            return option?.__id === 'hide_disabled';
        } else {
            if (_.find(clone, (v) => {
                return v.__id === option?.__id;
            })) {
                _.remove(clone, (vl) => {
                    return vl.__id === option?.__id;
                });
            } else {
                clone.push(option);
            }
        }
        props.onChange(clone, e);
    };
    const filterOptions = (options, {inputValue}) => {
        const iv = inputValue.toUpperCase();
        return _.filter(options, (o) => {
            return o?.__id === 'select_none' || o?.__id === 'hide_disabled' || (o?.[props.optionLabel].toUpperCase().indexOf(iv) !== -1);
        });
    };

    return (
        <FormControl fullWidth={props.fullWidth}>
            {/*<Autocomplete*/}
            <AgyleDictionaryField
                size={'small'}
                options={options}
                name={props.name}
                disabled={props.disabled}
                clearOnBlur
                clearOnEscape
                className={clsx(classes.border, props.size, classes.menu)}
                getOptionLabel={(option) => option?.[props.optionLabel]}
                style={{
                    width: '100%',
                    ...props.style,
                }}
                textFieldClass={classes.textBox}
                inputLabelClass={classes.label}
                noBorder={false}
                filterOptions={filterOptions}
                placeholder={textValue}
                dropdownStyle={classes.menu}
                paperBackground
                withNewBackground={props.withNewBackground}
                endAdornment={
                        <InputAdornment position="end">
                            {values.length > 1 && (
                                <div className={clsx(classes.box, 'background', classes.label, props.size)}>
                                    {values.length}
                                </div>)}
                            {values.length <= 1 && (
                                <div className={clsx(classes.box, classes.label, props.size)}>
                                    <ArrowDropDownIcon/>
                                </div>)}
                        </InputAdornment>
                }
                // renderInput={(params) => (
                //     <TextField
                //         {...params}
                //         className={classes.textBox}
                //         placeholder={textValue}
                //         sx={{label: {color: 'blue'}}}
                //         InputProps={{
                //             ...params.InputProps,
                //             classes: {input: classes.textBox},
                //             sx: {
                //                 borderRadius: 2,
                //
                //             },
                //             endAdornment:
                //                 <InputAdornment position="end">
                //                     {values.length > 1 && (
                //                         <div className={clsx(classes.box, 'background', classes.label, props.size)}>
                //                             {values.length}
                //                         </div>)}
                //                     {values.length <= 1 && (
                //                         <div className={clsx(classes.box, classes.label, props.size)}>
                //                             <ArrowDropDownIcon/>
                //                         </div>)}
                //                 </InputAdornment>,
                //         }}
                //     />
                // )}
                sx={{
                    color: '#FFFFFF',
                    opacity: 1,
                    '.MuiOutlinedInput-notchedOutline': {
                        borderColor: props.value.length === 0 ? (theme.palette.mode === 'light' ? '#acb1b5' : '#626466') : '#2180F7',
                    },
                    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#2180F7',
                    },
                    '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: theme.palette.mode === 'light' ? '#626466' : '#ACAFB3',
                    },
                    '&.Mui-focused .MuiSvgIcon-root ': {
                        fill: '#2180F7 !important',
                    },
                    '& .MuiInputBase-input::placeholder': {
                        opacity: 1,
                        color: theme.palette.mode === 'light' ? "#000": '#FFFFFF'
                    },
                }}
                renderOption={(_optionProps, option, {selected}) => {
                    const {key, ...restProps} = _optionProps;
                    return (
                        <Fragment key={option?.__id}>
                            <MenuItem
                                key={option?.__id}
                                className={classes.menuItem}
                                onClick={(e) => {
                                    selectItem(option, e);
                                }}
                            >
                                <Checkbox
                                    checked={option?.__id === 'hide_disabled' ? hideDisabled : valuesMap?.[props.pathToItemValue ? option?.[props.pathToItemValue] : option] === 1 && !option?.disabled}
                                    disableRipple
                                    disabled={option?.disabled}
                                />
                                <div
                                    className={option?.disabled ? classes.optionDisabled : null}> {option?.[props.optionLabel]} {!props.optionActiveLabel || option?.active || '(inactive)'}</div>

                            </MenuItem>
                            {(props.dividerAfterSelectAll && props.showSelectNone && props.hideOption ? option?.__id === 'hide_disabled' : option?.__id === 'select_none') &&
                                <Divider key={'!divder!'} className={classes.divider}/>
                            }
                        </Fragment>
                    );
                }}
            >
            </AgyleDictionaryField>
        </FormControl>
    );
};

export default AgyleMultiselectAutocomplete;

AgyleMultiselectAutocomplete.propTypes = {
    label: PropTypes.string,
    name: PropTypes.string,
    onChange: PropTypes.func.isRequired,
    options: PropTypes.array.isRequired,
    disabled: PropTypes.bool,
    size: PropTypes.oneOf(['smaller', 'small', 'medium', 'large']),
    dividerAfterSelectAll: PropTypes.bool,
    showSelectAll: PropTypes.bool,
    showSelectNone: PropTypes.bool,
    rememberPreviousValue: PropTypes.bool,
    optionLabel: PropTypes.string,
    optionActiveLabel: PropTypes.bool,
    comparePath: PropTypes.string,
    pathToItemValue: PropTypes.string,
    fullObjecValue: PropTypes.bool,//if true and pathToItemValue is set onChange will return array of full objects not only array of 'ids'
    fullWidth: PropTypes.bool,
    autocomplete: PropTypes.bool,
    hideOption: PropTypes.bool,
    hideOptionInitialState: PropTypes.bool,
    hideDisabledLabel: PropTypes.string,
};

AgyleMultiselectAutocomplete.defaultProps = {
    label: '',
    disabled: false,
    size: 'medium',
    showSelectAll: true,
    showSelectNone: true,
    rememberPreviousValue: true, // Memoize selected value before select all or select none is clicked
    dividerAfterSelectAll: true,
    optionLabel: '__description',
    comparePath: '__id',
    fullObjecValue: false,
    fullWidth: true,
    autocomplete: false,
    hideOption: false,
    hideOptionInitialState: true,
    hideDisabledLabel: 'Hide unavailable',
};